import type { BookingFlags, BookingTagMeta } from "@/types/booking";

/** Short labels for tag badges (filter values stay the raw API tag). */
export const BOOKING_TAG_LABELS: Record<string, string> = {
  PENDING_RELEASE: "Pending release",
  REBOOKED: "Rebooked",
  DELAYED: "Delayed",
  ROLLED: "Rolled",
  Rolled: "Rolled",
  REEFER: "Reefer",
  Reefer: "Reefer",
  HEAVY: "Heavy",
  DOCUMENTS_ARRIVED: "Documents arrived",
  DOCUMENTS_MISSING: "Documents missing",
  Opportunity: "Opportunity",
};

/** Fallback copy when the API did not attach a per-booking tooltip. */
export const BOOKING_TAG_TOOLTIPS: Record<string, string> = {
  DG: "Dangerous goods",
  MSDS: "Material safety data sheet required",
  REEFER: "Reefer (temperature-controlled)",
  Reefer: "Reefer (temperature-controlled)",
  OOG: "Out of gauge",
  HEAVY: "Heavy container (over 21t per unit)",
  ROLLED: "Rolled to a later sailing",
  Rolled: "Rolled to a later sailing",
  PENDING_RELEASE: "Pending carrier release",
  REBOOKED: "Rebooked to a different contract",
  DELAYED: "Departure delayed",
  EXW: "Ex-Works",
  "T/S": "Transshipment",
  DOCUMENTS_ARRIVED: "Bill of lading document present for this booking",
  DOCUMENTS_MISSING: "Shipped or delivered with no bill of lading document found",
  Opportunity: "Rebook opportunity",
};

function flagTagName(item: BookingTagMeta | string): string {
  return typeof item === "string" ? item : item.tag;
}

function flagTooltip(item: BookingTagMeta | string): string | undefined {
  if (typeof item === "string") return undefined;
  const tip = item.tooltip?.trim();
  return tip || undefined;
}

/** First matching `BookingTag` across contract_ref keys (legacy strings included). */
export function resolveTagMeta(
  tag: string,
  flags?: BookingFlags,
): BookingTagMeta | undefined {
  if (!flags) return undefined;
  for (const arr of Object.values(flags)) {
    if (!Array.isArray(arr)) continue;
    for (const item of arr) {
      if (flagTagName(item) === tag) {
        return typeof item === "string" ? { tag } : item;
      }
    }
  }
  return undefined;
}

/** Human-readable badge text; acronyms (DG, OOG) and short tags pass through. */
export function formatBookingTagLabel(tag: string): string {
  const mapped = BOOKING_TAG_LABELS[tag];
  if (mapped) return mapped;
  if (tag.includes("_")) {
    return tag
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
      .join(" ");
  }
  return tag;
}

/** Prefer the API tooltip; fall back to the catalog description. */
export function resolveTagTooltip(
  tag: string,
  flags?: BookingFlags,
): string | undefined {
  const meta = resolveTagMeta(tag, flags);
  return (meta ? flagTooltip(meta) : undefined) ?? BOOKING_TAG_TOOLTIPS[tag];
}
